/* eslint-disable no-param-reassign */
/**
 * KeyPad class
 */
import { filter, find, findLast, forEach, isEmpty } from 'lodash';
import { BehaviorSubject } from 'rxjs';

import { KeypadAction, KeypadValueType } from './KeyPad.enums';
import { KeypadConfigType } from '../../../interfaces/key-pad-config.enum';

import type {
  Fraction,
  KeypadEvent,
  KeypadOptions,
  KeypadOutputEvent,
  ValueRange,
} from './KeyPad.type';

export class KeyPad {
  public readonly state: BehaviorSubject<KeypadOutputEvent> =
    new BehaviorSubject(null);
  public isInitialValue = false;
  public isDefaultMinValue = false;
  public isDefaultValue = false;

  private readonly zeroValue: string = '0';
  private readonly dot: string = '.';
  private readonly coma: string = ',';
  private readonly symb: RegExp = /[\.\,]+/;
  private readonly digitsRegex = '^\\d+$';
  private readonly defaultOptions: KeypadOptions = {
    keyPadType: KeypadConfigType.INTEGER,
    length: {
      min: 0,
      max: 10,
    },
    precision: 0,
    supportsAlphabeticSymbols: false,
  };
  private defaultMinVal = '';
  private defaultVal = '';
  private keyPadType: KeypadValueType;
  private supportsAlphabeticSymbols: boolean;
  private value: string[] = [];
  private initialValue: string[];
  private digitsAfterDot: number;
  private ignoreSecondDot = false;
  private isIntegerLocked = false;
  private fractions: Fraction[];
  private length: ValueRange;
  private multiplier = 100;

  public initialize(options: KeypadOptions = this.defaultOptions): void {
    this.keyPadType = <KeypadValueType>options.keyPadType;
    this.supportsAlphabeticSymbols = options.supportsAlphabeticSymbols;
    this.setKeyPadPrecision(options);
    if (options.precision) {
      this.multiplier = 10 ** options.precision;
    }
    this.fractions = this.createFractions(this.digitsAfterDot);
    this.length = options.length;
    this.defaultMinVal = options.defaultMinVal;
    this.defaultVal = options.defaultVal;
    this.isIntegerLocked = false;
    if (this.keyPadType !== KeypadValueType.STRING) {
      const startVal: string = this.defaultMinVal
        ? this.defaultMinVal
        : this.zeroValue;
      this.setValue(Number(startVal));
      if (this.defaultMinVal) {
        this.isDefaultMinValue = true;
      }

      if (this.defaultVal) {
        this.isDefaultValue = true;
      }
    }
    this.updateState();
  }

  public destroy(): void {
    this.fractions = this.resetFractions(this.fractions);
    this.state.complete();
  }

  public onKeyPadEvent(event: KeypadEvent): void {
    if (
      event &&
      (event.action === KeypadAction.POP || event.action === KeypadAction.PUSH)
    ) {
      this[event.action](event.payload);
      this.updateState();
    } else {
    }
  }

  public updateState(): void {
    this.state.next({
      asNumber: this.getNumberValue(),
      asString: this.getStringValue(),
      isDefaultMinValue: this.isDefaultMinValue,
      isDefaultValue: this.isDefaultValue,
    });
  }

  public setValue(
    value: number,
    returnInitialValue = false,
    isReset = false,
  ): void {
    if (this.keyPadType !== KeypadValueType.STRING) {
      if (!isReset && this.defaultVal && value === +this.defaultVal) {
        this.isDefaultValue = true;
      } else {
        this.isDefaultValue = false;
      }

      if (!isReset && this.defaultMinVal && value < +this.defaultMinVal) {
        this.isDefaultMinValue = true;
        value = +this.defaultMinVal;
      } else {
        this.isDefaultMinValue = false;
      }
    }

    this.value = this.toString(value)
      .split('')
      .filter((char: string): boolean => char !== this.getDotIfNeed());

    if (returnInitialValue) {
      this.isInitialValue = true;
      this.initialValue = [...this.value];
    }
  }

  public normalizeNumber(value: number): string {
    return this.addComa(value.toFixed(this.digitsAfterDot));
  }

  public isNewValue(value: number): boolean {
    if (this.keyPadType === KeypadValueType.STRING) {
      return this.state.getValue()?.asString !== value.toString();
    }
    return this.state.getValue()?.asString !== this.normalizeNumber(value);
  }

  public resetInitialValue(): void {
    this.isInitialValue = false;
    this.isIntegerLocked = false;
  }

  public getNumberValue(value: string = this.getStringValue()): number {
    return Number(value.split(this.coma).join(''));
  }

  private push(value: string): void {
    if (this.keyPadType === KeypadValueType.STRING) {
      if (
        !this.supportsAlphabeticSymbols &&
        !new RegExp(this.digitsRegex).test(value)
      ) {
        return;
      }

      if (this.length && this.value.length >= this.length.max) {
        return;
      }

      this.value.push(value);
      return;
    }
    value = value.replace(this.coma, '');

    const isValueDot: boolean = value === this.dot;

    if (isValueDot && this.ignoreSecondDot) {
      return;
    }

    if (
      this.getValueLengthWithoutDot() + value.length > this.length.max &&
      !this.isIntegerLocked
    ) {
      return;
    }

    // reset value straight to the zero after removing initial value
    if (this.isInitialValue || this.isDefaultMinValue) {
      this.setValue(0, false, true);
    } else {
      this.isDefaultMinValue = false;
      this.isDefaultValue = false;
    }

    if (this.isEmpty() && !this.ignoreSecondDot) {
      this.isIntegerLocked = false;
      this.fractions = this.resetFractions(this.fractions);
    }

    this.ignoreSecondDot = isValueDot;

    this.isInitialValue = false;

    if (isValueDot && !this.isIntegerLocked) {
      this.lockIntegerIfNeeded();

      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      !this.isEmpty() ? this.setValue(this.multiplyValue()) : this.setValue(0);

      return;
    }

    if (!this.isIntegerLocked) {
      const newValue = Number([...this.value, ...value.split('')].join(''));
      if (this.defaultMinVal && newValue < +this.defaultMinVal) {
        this.isDefaultMinValue = true;
        value = this.defaultMinVal;
      }

      this.value.push(...value.split(''));
      return;
    }

    let availableFractions: Fraction[];

    if (isValueDot) {
      return;
    }

    // it's double zero case ("00")
    if (value.length > 1) {
      availableFractions = filter(
        this.fractions,
        (fraction: Fraction) => !fraction.isSet,
      );
    } else {
      const availableFraction: Fraction = find(
        this.fractions,
        (fraction: Fraction) => !fraction.isSet,
      );

      if (availableFraction) {
        availableFractions = [availableFraction];
      }
    }

    if (isEmpty(availableFractions)) {
      return;
    }

    forEach(availableFractions, (fraction: Fraction) => {
      fraction.isSet = true;
      fraction.value = value[0];
    });

    this.value = [...this.getDigit(), ...this.getFractionsValues()];
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private pop(value?: string): void {
    if (this.keyPadType === KeypadValueType.STRING) {
      this.value.pop();
      return;
    }

    if (this.isEmpty() && !this.fractionsHaveBeenSet()) {
      if (this.isIntegerLocked) {
        this.isIntegerLocked = false;
      } else {
        this.value = this.initialValue ? [...this.initialValue] : this.value;
        this.fractions = this.resetFractions(this.fractions);
        this.isInitialValue = true;
      }
      return;
    }

    const simplePop = (): void => {
      this.value = [this.zeroValue, ...this.value.slice(0, -1)];
      this.isInitialValue = false;
    };

    if (!this.isIntegerLocked) {
      if (this.defaultVal && this.getStringValue().length === 1) {
        this.isDefaultValue = false;
      }

      if (this.defaultMinVal && this.getStringValue().length === 1) {
        this.value = [this.zeroValue, this.defaultMinVal];
        this.isInitialValue = false;
        this.isDefaultMinValue = true;
      } else {
        simplePop();
      }
    } else {
      const fractionForPop: Fraction = findLast(
        this.fractions,
        (fraction: Fraction) => fraction.isSet,
      );

      if (fractionForPop) {
        fractionForPop.isSet = false;
        fractionForPop.value = this.zeroValue;
      } else {
        this.isIntegerLocked = false;
        simplePop();
        return;
      }
      this.value = [...this.getDigit(), ...this.getFractionsValues()];
    }
  }

  private getStringValue(): string {
    if (this.keyPadType === KeypadValueType.STRING) {
      return this.value.join('');
    }

    const value = [
      ...this.getDigit(),
      this.getDotIfNeed(),
      ...this.removeFractionalZeros(),
    ].join('');

    if (value === this.dot) {
      return this.normalizeNumber(Number(this.zeroValue));
    }

    return this.normalizeNumber(Number(value));
  }

  private getValueLengthWithoutDot(): number {
    return this.getStringValue().split(this.symb).join('').length;
  }

  private multiplyValue() {
    // multiply by proper multiplier due to
    // max number length restriction
    return this.length.max - this.getValueLengthWithoutDot() === 1
      ? this.getPartlyMultipliedValue()
      : this.getNumberValue() * this.multiplier;
  }

  private getPartlyMultipliedValue() {
    const stringValue = this.getStringValue().replace(this.coma, '');
    const value = stringValue.slice(0, -1);

    // we need to get last number to be able to set it as first fraction
    // number after multiplying
    const lastValue: string = stringValue.slice(-1);

    const digits = Number(value) * (this.multiplier / 10);
    const fraction = Number(lastValue);
    this.fractions[0].value = fraction.toString();
    this.fractions[0].isSet = true;

    return Number(digits + this.getDotIfNeed() + fraction);
  }

  private removeFractionalZeros(): string[] {
    const fraction: string[] = this.getFraction();

    // don't do special operation with fraction part for integer keypad
    if (this.isIntegerType || this.isNumericStringType || isEmpty(fraction)) {
      return fraction;
    }
    const revertedFraction = fraction.reverse();
    const cleanNumber = Number(revertedFraction.join(''));

    if (!cleanNumber) {
      return [];
    }

    return cleanNumber.toString().split('').reverse();
  }

  private isEmpty(): boolean {
    return this.sum(this.value) === 0;
  }

  private sum(array: string[]): number {
    return array.reduce((p: number, n: string): number => p + Number(n), 0);
  }

  private getFraction(): string[] {
    return this.digitsAfterDot ? this.value.slice(-this.digitsAfterDot) : [];
  }

  private getDigit(): string[] {
    return this.digitsAfterDot
      ? this.value.slice(0, -this.digitsAfterDot)
      : this.value;
  }

  private toString(value: number): string {
    return value.toFixed(this.digitsAfterDot);
  }

  private getDotIfNeed(): string {
    return this.isIntegerType ||
      this.isNumericStringType ||
      !this.digitsAfterDot
      ? ''
      : this.dot;
  }

  private get isIntegerType(): boolean {
    return this.keyPadType === KeypadValueType.INTEGER;
  }

  private get isNumericStringType(): boolean {
    return this.keyPadType === KeypadValueType.STRING;
  }

  private fractionsHaveBeenSet(): boolean {
    return Boolean(
      find(this.fractions, (fraction: Fraction) => fraction.isSet),
    );
  }

  private getFractionsValues(): string[] {
    return this.fractions.map((fraction: Fraction) => fraction.value);
  }

  private addComa(value: string): string {
    return value.replace(
      /(\d)(?=(?:\d{3})+(?:\.|$))|(\.\d{1,})\d*$/g,
      (m, s1, s2) => s2 || s1 + this.coma,
    );
  }

  private createFractions(digitsAfterDot: number): Fraction[] {
    const fractions: Fraction[] = [];

    while (digitsAfterDot) {
      fractions.unshift({
        position: digitsAfterDot - 1,
        value: this.zeroValue,
        isSet: false,
      });
      digitsAfterDot -= 1;
    }
    return fractions;
  }

  private resetFractions(fractions: Fraction[]): Fraction[] {
    if (!fractions || !fractions.length) {
      return [];
    }

    return fractions.map(
      (fraction: Fraction): Fraction => ({
        ...fraction,
        isSet: false,
        value: this.zeroValue,
      }),
    );
  }

  private setKeyPadPrecision(keyPadOptions: KeypadOptions): void {
    this.digitsAfterDot =
      this.keyPadType === KeypadValueType.FLOAT
        ? keyPadOptions.precision
        : Number(this.zeroValue);
  }

  private lockIntegerIfNeeded(): void {
    this.isIntegerLocked = Boolean(this.digitsAfterDot);
  }
}
